import React from 'react';
import { View, Text, TouchableOpacity, Modal } from 'react-native';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

type EditModeAction = 'this' | 'future' | 'cancel';

interface EditModeModalProps {
  visible: boolean;
  title?: string;
  description?: string;
  thisLabel?: string;
  futureLabel?: string;
  onSelect: (action: EditModeAction) => void;
  destructive?: boolean;
}

export default function EditModeModal({
  visible,
  title = 'Edit Recurring Activity',
  description = 'How should this change be applied?',
  thisLabel = 'This activity only',
  futureLabel = 'All future activities',
  onSelect,
  destructive = false,
}: EditModeModalProps) {
  const styles = useThemedStyles(createStyles);

  return (
    <Modal visible={visible} transparent animationType="fade" accessibilityViewIsModal={true}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <Text style={styles.title} accessibilityRole="header">{title}</Text>
          <Text style={styles.description}>{description}</Text>

          <TouchableOpacity
            style={styles.option}
            onPress={() => onSelect('this')}
            accessibilityLabel={thisLabel}
            accessibilityHint="Changes apply to this occurrence only"
            accessibilityRole="button"
            testID="edit-mode-modal.this-button"
          >
            <Text style={styles.optionText} accessible={false}>{thisLabel}</Text>
            <Text style={styles.optionHint} accessible={false}>
              Changes apply to this occurrence only
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.option, destructive && styles.optionDestructive]}
            onPress={() => onSelect('future')}
            accessibilityLabel={futureLabel}
            accessibilityHint="Changes apply to all future occurrences"
            accessibilityRole="button"
            testID="edit-mode-modal.future-button"
          >
            <Text style={[styles.optionText, destructive && styles.optionTextDestructive]} accessible={false}>
              {futureLabel}
            </Text>
            <Text style={styles.optionHint} accessible={false}>
              Changes apply to all future occurrences
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.cancelButton}
            onPress={() => onSelect('cancel')}
            accessibilityLabel="Cancel"
            accessibilityRole="button"
            testID="edit-mode-modal.cancel-button"
          >
            <Text style={styles.cancelText} accessible={false}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (theme: Theme) => ({
  overlay: { flex: 1, backgroundColor: theme.colors.overlay, justifyContent: 'center' as const, alignItems: 'center' as const, padding: 24 },
  card: { backgroundColor: theme.colors.surface, borderRadius: 16, padding: 24, width: '100%' as const, maxWidth: 400 },
  title: { fontSize: 18, fontWeight: '700' as const, color: theme.colors.textPrimary, marginBottom: 8, textAlign: 'center' as const },
  description: { fontSize: 14, color: theme.colors.textMuted, textAlign: 'center' as const, marginBottom: 20 },
  option: { backgroundColor: theme.colors.bgTertiary, borderRadius: 12, padding: 16, marginBottom: 10 },
  optionDestructive: { backgroundColor: theme.colors.errorBg },
  optionText: { fontSize: 16, fontWeight: '600' as const, color: theme.colors.textPrimary, marginBottom: 4 },
  optionTextDestructive: { color: theme.colors.error },
  optionHint: { fontSize: 13, color: theme.colors.textMuted },
  cancelButton: { paddingVertical: 14, alignItems: 'center' as const, marginTop: 4 },
  cancelText: { fontSize: 16, fontWeight: '500' as const, color: theme.colors.textMuted },
});
